/*
 * Copyright (c) 2024. Amazon.com, Inc. or its affiliates. All rights reserved.
 */

import { type PlaylistController, type PlaylistEntry } from './PlaylistController'
import { logError, noop, type Unsubscribe } from '@amazon/vinyl-util'
import { ctx } from '@/util/context'
import { formatError } from '@/errorHandler'
import { toastError } from '@/components/toast'
import { getStringAsync } from '@/playback/i18n/bundle.generated'
import type { Player } from '@/playback/api'
import { createDisposer } from '@amazon/vinyl-util'
import { onPipClose, setHasVideo } from '@/video/videoStage'
import { savePodcastEpisodeProgress } from '@/podcast/podcastEpisodeProgressService'
import {
    clearVideoPlaybackProgress,
    readVideoPlaybackProgress,
    saveVideoPlaybackProgress,
} from '@/video/videoPlaybackProgressService'
import { initializePlaybackConcurrencyBinding } from '@/playback/concurrency/playbackConcurrencyBinding'
import { maybeStartAutoplayContinuation } from '@/playback/autoplayContinuation'
import type { CloudQueueController } from '@/cloudQueue/CloudQueueController'
import {
    currentMeasureToken,
    markPlaying,
    type PlaybackMeasureToken,
} from '@/perf/playbackDelayMarks'

/**
 * How often the background tick persists playback progress while a
 * resumable entry (podcast episode or music video) is actively
 * playing. Mirrors GH's periodic save cadence — frequent enough that
 * a browser crash loses at most a few seconds of "resume where you
 * left off" fidelity, sparse enough that the save isn't spamming the
 * backing store.
 */
const PROGRESS_TICK_MS = 15_000

/**
 * Skip resume-seek when the stored offset is within the first few
 * seconds — no user-visible difference from starting from 0, and
 * spares a needless seek during load.
 */
const MIN_RESUME_MS = 3_000

/**
 * Treat a video within this many seconds of its end as "finished". A
 * finished video must clear its stored offset rather than persist a
 * ~duration one: unlike podcasts (whose resume reads a Firefly wire
 * that clears on completion), video resume is a pure client round-trip,
 * so a stored ≈duration offset would seek the next play straight to the
 * end and it could never restart from 0.
 */
const VIDEO_NEAR_COMPLETE_SEC = 10

function isPodcastEpisode(entry: PlaylistEntry | null): boolean {
    return entry?.entity.__typename === 'PodcastEpisode'
}

function isVideoEntry(entry: PlaylistEntry | null): boolean {
    return entry?.video === true
}

/**
 * Stable resume-store key for a video: the entity asin, matching the
 * id DMCS keys on in the concurrency binding. `entry.id` is unusable
 * here — it falls back to a throwaway `createShortUid()` for id-less
 * videos, so keying on it never round-trips a resume and leaks dead
 * LRU keys. No stable id means "don't persist".
 */
function videoStableId(entry: PlaylistEntry): string | null {
    return entry.entity.id || null
}

function isVideoNearComplete(currentSec: number, durationSec: number): boolean {
    if (!(durationSec > 0)) return false
    return currentSec >= durationSec - VIDEO_NEAR_COMPLETE_SEC || currentSec >= durationSec * 0.98
}

/**
 * Whether an active PiP window should close now that the current entry
 * changed. Only a switch to *different* media closes it — a re-queue of
 * the same asin (e.g. a re-buffer that re-emits `current`) leaves the
 * floating window intact.
 */
export function shouldExitPipOnMediaChange(
    pipActive: boolean,
    previousMediaId: string | null,
    nextMediaId: string | null
): boolean {
    return pipActive && nextMediaId !== previousMediaId
}

/**
 * Read the persisted playback offset for a podcast episode from the
 * wire. Firefly is the source of truth: the offset comes off the
 * episode's playbackInformation.progressMilliseconds, saved on the
 * prior session. No saved position means start from 0.
 */
function readPodcastResumeMs(entry: PlaylistEntry): number {
    if (entry.entity.__typename !== 'PodcastEpisode') return 0
    const wire = entry.entity.playbackInformation?.progressMilliseconds
    return typeof wire === 'number' && wire > 0 ? wire : 0
}

/**
 * Resume offset (ms) for whichever resumable entry types we persist.
 * Podcasts read off the wire; videos read the client-side store (the
 * Video schema has no playbackInformation). Everything else — music
 * tracks — always starts at 0.
 */
function readResumeMs(entry: PlaylistEntry): number {
    if (isPodcastEpisode(entry)) return readPodcastResumeMs(entry)
    if (isVideoEntry(entry)) {
        const id = videoStableId(entry)
        return id ? readVideoPlaybackProgress(id) : 0
    }
    return 0
}

const { target, errorHandler } = ctx('playerPlaylistBinding')

export interface PlaylistPlayerBindingsDeps {
    readonly playerPromise: Promise<Player>
    readonly playlistController: PlaylistController
    readonly cloudQueueController: CloudQueueController
}

/**
 * Initializes bindings for PlayListController <--> VinylAmznPlayer
 */
export function initializePlaylistPlayerBindings(deps: PlaylistPlayerBindingsDeps): Unsubscribe {
    const disposer = createDisposer()
    const { add, dispose } = disposer

    const { playerPromise, playlistController } = deps

    // Local-device stream-concurrency (DMCS) reporting + take-over prompt.
    // Self-contained state machine; kept off the transport path here.
    add(initializePlaybackConcurrencyBinding(deps))

    // Native PiP X-button → unload the playlist. Without this, dismissing
    // the floating window would only exit PiP and the audio/video would
    // keep playing in the transport — the user's intent ("close this")
    // is clearly to stop, not to keep playing in the background.
    add(
        onPipClose(() => {
            playlistController.clear()
        })
    )

    // Starting different media while the video is handed off to PiP closes
    // the floating window — the old picture is no longer what's playing.
    let pipMediaId: string | null = playlistController.current.value?.entity.id ?? null
    add(
        playlistController.current.onData((entry) => {
            const id = entry?.entity.id ?? null
            if (shouldExitPipOnMediaChange(!!document.pictureInPictureElement, pipMediaId, id)) {
                document.exitPictureInPicture().catch(noop)
            }
            pipMediaId = id
        })
    )

    /**
     * Anchor the cloud queue's gateway-side cursor so mutations
     * that need a "current entity" (setQueueShuffleMode,
     * setQueueLoopMode, …) succeed. Mirrors hornet's
     * CloudQueueStore.reportDevicePlayback{Playing,Paused,Ended}
     * lifecycle. Best-effort: any error is logged and swallowed —
     * a reporting blip shouldn't break playback.
     */
    function reportPlayback(state: 'PLAYING' | 'STOPPED' | 'ENDED', progressInMs?: number) {
        const queueId = playlistController.cloudQueueOptions.value?.queueId
        const entityRef = playlistController.current.value?.entityReferenceId
        if (!queueId || !entityRef) return
        const payload = {
            queueId,
            entityReferenceId: entityRef,
            playbackState: state,
            ...(progressInMs != null ? { progressInMs } : null),
        }
        // Lazy — the queue-manipulation service only matters once a cloud
        // queue is live (queueId set), so keep it off the app-shell chunk.
        import('@/cloudQueue/cloudQueueService')
            .then(({ reportCloudQueuePlayback }) => reportCloudQueuePlayback(payload))
            .catch(noop)
    }

    playerPromise
        .then((player) => {
            if (disposer.disposed) return
            // Progress tick: only runs while a resumable entry (podcast
            // episode or music video) is playing. Cleared on entry
            // change / pause / dispose.
            let progressTickHandle: ReturnType<typeof setInterval> | null = null
            const clearProgressTick = () => {
                if (progressTickHandle != null) {
                    clearInterval(progressTickHandle)
                    progressTickHandle = null
                }
            }
            const saveProgressForCurrent = () => {
                const entry = playlistController.current.value
                if (!entry) return
                const currentSec = player.contentTime$.value || 0
                const ms = Math.floor(currentSec * 1000)
                if (isPodcastEpisode(entry)) {
                    if (!entry.id) return
                    savePodcastEpisodeProgress(entry.id, ms)
                } else if (isVideoEntry(entry)) {
                    const id = videoStableId(entry)
                    if (!id) return
                    // A finished video clears its offset so it can restart
                    // from 0; a mid-video pause persists a real resume point.
                    if (isVideoNearComplete(currentSec, entry.duration)) {
                        clearVideoPlaybackProgress(id)
                    } else {
                        saveVideoPlaybackProgress(id, ms)
                    }
                }
            }
            const startProgressTickIfNeeded = () => {
                clearProgressTick()
                const entry = playlistController.current.value
                if (!isPodcastEpisode(entry) && !isVideoEntry(entry)) return
                progressTickHandle = setInterval(saveProgressForCurrent, PROGRESS_TICK_MS)
            }
            add(clearProgressTick)

            // The delegate owns feeding the engine (activate + play) off the
            // `current` subscription; this binding is engine-agnostic and only
            // reflects the singleton `<video>` presence + arms a resume seek per
            // entry. Resume fires once the content's duration is known (or a break
            // is running) so the offset lands against a real timeline.
            let resumeForEntry: PlaylistEntry | null = null
            // Engine-agnostic close of the initial-playback-delay window: adopt the
            // open window per current entry, then draw the span when whichever
            // engine reaches `playing` — so audiobooks (Audible) and videos draw the
            // total IPD span like music does, not just the Vinyl path.
            let ipdToken: PlaybackMeasureToken | null = null
            // The engine drops seeks during an ad, and Vinyl ends a track before its
            // postroll completes, so a seek requested mid-break runs once the break ends.
            let deferredSeek: {
                readonly entry: PlaylistEntry | null
                readonly seek: () => void
            } | null = null
            const seekAfterAd = (seek: () => void) => {
                if (player.ads.adState$.value) {
                    deferredSeek = { entry: playlistController.current.value, seek }
                } else {
                    seek()
                }
            }
            // Seek the armed entry to its stored offset so a podcast episode /
            // music video picks up where the user left off. Music tracks are
            // never armed (readResumeMs returns 0) — they start at 0.
            const fireResumeSeek = () => {
                const entry = resumeForEntry
                if (!entry || playlistController.current.value !== entry) return
                resumeForEntry = null
                seekAfterAd(() => {
                    player
                        .seekTo(readResumeMs(entry) / 1000, { reason: 'resume' })
                        .catch(errorHandler)
                })
            }
            add(
                playlistController.current.onData((currentEntry) => {
                    setHasVideo(currentEntry?.video === true)
                    clearProgressTick()
                    ipdToken = currentEntry ? currentMeasureToken() : null
                    resumeForEntry =
                        currentEntry && readResumeMs(currentEntry) >= MIN_RESUME_MS
                            ? currentEntry
                            : null
                    // A paused page-reload restore parks `current` without
                    // playing — don't anchor the gateway cursor as PLAYING for
                    // it. The first real play re-reports PLAYING (see playing$).
                    if (currentEntry && !playlistController.restoringCurrent) {
                        reportPlayback('PLAYING', 0)
                    }
                    // A known duration won't re-fire contentDuration$; an ad here is the outgoing entry's.
                    if (
                        resumeForEntry &&
                        !player.ads.adState$.value &&
                        !Number.isNaN(player.contentDuration$.value)
                    ) {
                        fireResumeSeek()
                    }
                })
            )
            add(
                player.contentDuration$.onData((duration) => {
                    if (Number.isNaN(duration)) return
                    fireResumeSeek()
                })
            )
            // Only a break's first tick arms the resume: an outgoing entry's ad still
            // ticks under the next entry until its engine resets.
            let adShowing = false
            add(
                player.ads.adState$.onData((adState) => {
                    const breakStarted = adState != null && !adShowing
                    adShowing = adState != null
                    if (adState) {
                        if (breakStarted) fireResumeSeek()
                        return
                    }
                    const pending = deferredSeek
                    deferredSeek = null
                    if (pending && pending.entry === playlistController.current.value) {
                        pending.seek()
                    }
                })
            )

            // Keep the gateway in sync as the player transitions in/out of
            // playing. `playing$` is true only while actively producing audio.
            // Skip the synchronous initial emission — a genuine pause/stop only
            // matters after playback has actually started (mirrors the old
            // event-based binding, which never fired on subscribe).
            let sawPlaying = false
            add(
                player.playing$.onData((playing) => {
                    if (playing) {
                        sawPlaying = true
                        markPlaying(playlistController.current.value?.title, ipdToken ?? undefined)
                        reportPlayback('PLAYING', player.contentTime$.value * 1000)
                        startProgressTickIfNeeded()
                        drainPendingSeek()
                    } else if (sawPlaying) {
                        // Persist the offset the instant playback halts so a
                        // browser close right after doesn't lose it.
                        saveProgressForCurrent()
                        clearProgressTick()
                        // Only a real pause reports STOPPED. A cast handoff
                        // swaps the active engine and unloads the outgoing one,
                        // clearing its duration — that's not a user stop, so
                        // skip the spurious STOPPED metric for it.
                        if (!Number.isNaN(player.duration$.value)) {
                            reportPlayback('STOPPED', player.contentTime$.value * 1000)
                        }
                    }
                })
            )
            // Drain any queued seek (e.g. from a `?t=90` deep-link) once the
            // player is playing. Cleared on dispatch so a subsequent track
            // change starts from 0 unless a fresh pendingSeek is posted.
            function drainPendingSeek() {
                const seconds = playlistController.pendingSeekSeconds.value
                if (seconds == null) return
                seekAfterAd(() => {
                    player.seekTo(seconds).catch(errorHandler)
                })
                // Deferred: a `pendingSeek()` posted while already playing
                // drains through this same cell's own `onData` — writing it
                // back synchronously here would be a reentrant write into a
                // cell still mid-notify, which the observable forbids.
                queueMicrotask(() => {
                    playlistController.pendingSeekSeconds.value = null
                })
            }
            add(
                playlistController.pendingSeekSeconds.onData((seconds) => {
                    if (seconds == null) return
                    if (player.playing$.value) drainPendingSeek()
                })
            )
            // A load/decode failure surfaces on the `error$` cell, not a
            // rejected command promise, so the transport otherwise sits stuck on
            // the failed track with no feedback. Toast the user.
            add(
                player.error$.onData((err) => {
                    if (!err) return
                    // Log the error for triage, but never surface it to the user —
                    // every other toast is localized.
                    logError(target, 'player error', formatError(err.cause ?? err))
                    const title = playlistController.current.value?.title
                    toastError(
                        title
                            ? getStringAsync('dmusic_playback_failed_titled_toast', { title })
                            : getStringAsync('dmusic_playback_failed_generic_toast')
                    )
                })
            )
            add(
                player.ended$.onData((ended) => {
                    if (!ended) return
                    reportPlayback('ENDED', player.contentTime$.value * 1000)
                    // Reached the end — persist the final offset so the resume
                    // path no longer prompts a resume for it.
                    saveProgressForCurrent()
                    clearProgressTick()
                    // Repeat-one short-circuits the auto-advance: seek back to 0
                    // and resume. Repeat-all and off fall through to the normal
                    // feed advance — when the feed has no further entries, the
                    // controller emits null.
                    if (playlistController.repeatMode.value === 'one') {
                        seekAfterAd(() => {
                            player.seekTo(0).catch(errorHandler)
                            player.play().catch(errorHandler)
                        })
                        return
                    }
                    // Advance to the next entry; when the queue has none
                    // left (fell off the tail), try a GH-style autoplay
                    // continuation before letting playback stop.
                    playlistController
                        .skipNext()
                        .then((next) => {
                            if (next != null) return
                            return maybeStartAutoplayContinuation({
                                playlistController,
                                cloudQueueController: deps.cloudQueueController,
                            }).then(() => undefined)
                        })
                        .catch(errorHandler)
                })
            )
        })
        .catch(errorHandler)
    return dispose
}
